import * as THREE from 'three';

// --- Public interfaces ---

export interface VectorContext {
  values: number[];
  editIndex: number;
  fullFrom: number;
  fullTo: number;
}

export interface DragHandle {
  position: THREE.Vector3;
  axes: Array<{
    axis: THREE.Vector3;    // unit vector in world space
    sign: number;           // +1 or -1
    scaleFactor: number;    // 1 for non-centered, 2 for centered
    value: number;          // current param value
    paramFrom: number;
    paramTo: number;
    dimKey: string;         // links to dimension line
    vectorContext?: VectorContext;
    isCenteredParam?: boolean; // true for sphere/cylinder: use center-based drag
  }>;
}

export interface DimensionLine {
  start: THREE.Vector3;
  end: THREE.Vector3;
  offsetDir: THREE.Vector3;
  offsetDist: number;
  value: number;
  dimKey: string;
  paramFrom: number;
  paramTo: number;
  vectorContext?: VectorContext;
  /** Non-editable (e.g. origin-to-object measurement). Label click is disabled. */
  readonly?: boolean;
  /** Short text prefix on the label (e.g. "ΔX"). Numeric value is appended. */
  labelPrefix?: string;
}

export interface DimensionSetup {
  handles: DragHandle[];
  lines: DimensionLine[];
  workPlaneY: number;
  bbox?: THREE.Box3;
}

export type DimensionEditCallback = (from: number, to: number, value: string) => void;

// --- Internals ---

interface LabelEntry {
  element: HTMLDivElement;
  midpoint3D: THREE.Vector3;
  dimKey: string;
  paramFrom: number;
  paramTo: number;
  value: number;
  vectorContext?: VectorContext;
}

interface HandleEntry {
  element: HTMLDivElement;
  handle: DragHandle;
  position3D: THREE.Vector3;
}

interface DragState {
  handleEntry: HandleEntry;
  startMouse: { x: number; y: number };
  startPosition3D: THREE.Vector3;
  lockedAxisIndex: number | null;
  startValues: number[];
  pointerId: number;
  currentNewValue: number | null;
  // Multi-axis mode: corner handles drag both X and Z simultaneously
  multiAxis: boolean;
  currentNewValues: number[];
}

// Minimum arrow/extension size. For anything bigger than a few mm, we scale
// these proportionally to the scene diagonal (see showDimensions).
const EXTENSION_OVERSHOOT = 0.08;
const ARROW_SIZE = 0.06;
const ARROW_ANGLE = Math.PI / 9;  // wider arrowhead — easier to read
const AXIS_LOCK_THRESHOLD = 5;

export class DimensionOverlay {
  private container: HTMLElement;
  private camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  private scene: THREE.Scene;
  private onEdit: DimensionEditCallback;
  private onDragStateChange: (dragging: boolean) => void;

  private overlayDiv: HTMLDivElement;
  private labels: LabelEntry[] = [];
  private handles: HandleEntry[] = [];
  private lineGroup: THREE.Group | null = null;
  private lineMaterial: THREE.LineBasicMaterial;
  private arrowMaterial: THREE.MeshBasicMaterial;

  // Stored for live update during drag
  private storedLines: DimensionLine[] = [];
  private dimLineGeometry: THREE.BufferGeometry | null = null;
  private dimArrowGeometry: THREE.BufferGeometry | null = null;

  private snapEnabled = true;
  private snapStep = 0.1;

  // Axis lock: which axes are enabled for drag resizing (world-space keys)
  private axisEnabled = { x: true, y: true, z: true };
  // Temporary keyboard constraint: only allow this axis during current drag
  private keyboardAxisConstraint: 'x' | 'y' | 'z' | null = null;

  private dragState: DragState | null = null;
  private workPlaneY = 0;
  private workPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private raycaster = new THREE.Raycaster();

  // Wireframe preview during drag
  private originalBBox: THREE.Box3 | null = null;
  private previewWireframe: THREE.LineSegments | null = null;
  private previewWireframeMaterial: THREE.LineBasicMaterial;

  // Tooltip showing current value during drag
  private dragTooltip: HTMLDivElement | null = null;

  setCamera(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera): void {
    this.camera = camera;
  }

  constructor(
    container: HTMLElement,
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    scene: THREE.Scene,
    onEdit: DimensionEditCallback,
    onDragStateChange: (dragging: boolean) => void,
  ) {
    this.container = container;
    this.camera = camera;
    this.scene = scene;
    this.onEdit = onEdit;
    this.onDragStateChange = onDragStateChange;

    this.overlayDiv = document.createElement('div');
    this.overlayDiv.className = 'dimension-overlay';
    container.appendChild(this.overlayDiv);

    this.lineMaterial = new THREE.LineBasicMaterial({
      color: 0x000000,
      depthTest: false,
    });
    this.arrowMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      depthTest: false,
      side: THREE.DoubleSide,
    });
    this.previewWireframeMaterial = new THREE.LineBasicMaterial({
      color: 0xffaa00,
      depthTest: false,
    });
  }

  // --- Theme ---

  applyTheme(lineColor: number, arrowColor: number): void {
    this.lineMaterial.color.setHex(lineColor);
    this.arrowMaterial.color.setHex(arrowColor);
  }

  // --- Snap settings ---

  setSnapSettings(enabled: boolean, step: number): void {
    this.snapEnabled = enabled;
    this.snapStep = Math.max(0.01, step);
  }

  /** Lock/unlock axes for drag resizing. Parameters use SCAD axis names mapped to world axes. */
  setAxisLock(scadX: boolean, scadY: boolean, scadZ: boolean): void {
    this.axisEnabled.x = scadX;  // SCAD X → world x
    this.axisEnabled.z = scadY;  // SCAD Y → world z
    this.axisEnabled.y = scadZ;  // SCAD Z → world y
  }

  /** Set a temporary keyboard axis constraint (world-space axis). */
  setKeyboardAxisConstraint(axis: 'x' | 'y' | 'z' | null): void {
    this.keyboardAxisConstraint = axis;
  }

  getKeyboardAxisConstraint(): 'x' | 'y' | 'z' | null {
    return this.keyboardAxisConstraint;
  }

  isDragging(): boolean {
    return this.dragState !== null;
  }

  private snap(value: number): number {
    if (!this.snapEnabled || this.snapStep <= 0) return Math.max(0.1, value);
    // Use integer arithmetic to avoid floating-point division artifacts
    // (e.g. 0.95 / 0.1 = 9.4999... in JS, causing Math.round to give 9 instead of 10)
    const precision = (this.snapStep.toString().split('.')[1] || '').length;
    const scale = Math.pow(10, precision);
    const intStep = Math.round(this.snapStep * scale);
    const intValue = Math.round(value * scale);
    const snapped = Math.round(intValue / intStep) * intStep / scale;
    return Math.max(this.snapStep, snapped);
  }

  // --- Public API ---

  show(setup: DimensionSetup): void {
    this.clear();

    this.workPlaneY = setup.workPlaneY;
    this.workPlane.set(new THREE.Vector3(0, 1, 0), -this.workPlaneY);

    if (setup.lines.length === 0 && setup.handles.length === 0) return;

    // Store bbox and lines for live update during drag
    this.originalBBox = setup.bbox?.clone() ?? null;
    this.storedLines = setup.lines.map(l => ({
      ...l,
      start: l.start.clone(),
      end: l.end.clone(),
      offsetDir: l.offsetDir.clone(),
    }));

    // Scale arrow + extension overshoot proportional to the scene so
    // huge models don't show pinprick arrows and tiny models don't show
    // giant ones.
    const diag = setup.bbox
      ? setup.bbox.getSize(new THREE.Vector3()).length()
      : 10;
    const sceneArrow = Math.max(ARROW_SIZE, diag * 0.018);
    const sceneOvershoot = Math.max(EXTENSION_OVERSHOOT, diag * 0.012);

    // Build 3D dimension lines
    if (setup.lines.length > 0) {
      this.lineGroup = new THREE.Group();
      this.lineGroup.renderOrder = 10;

      const linePositions: number[] = [];
      const arrowPositions: number[] = [];

      for (const line of setup.lines) {
        const { start, end, offsetDir, offsetDist } = line;

        const offsetA = start.clone().addScaledVector(offsetDir, offsetDist);
        const offsetB = end.clone().addScaledVector(offsetDir, offsetDist);

        const extEndA = start.clone().addScaledVector(offsetDir, offsetDist + sceneOvershoot);
        const extEndB = end.clone().addScaledVector(offsetDir, offsetDist + sceneOvershoot);

        // Extension lines
        linePositions.push(start.x, start.y, start.z, extEndA.x, extEndA.y, extEndA.z);
        linePositions.push(end.x, end.y, end.z, extEndB.x, extEndB.y, extEndB.z);

        // Measurement line
        linePositions.push(offsetA.x, offsetA.y, offsetA.z, offsetB.x, offsetB.y, offsetB.z);

        // Filled arrow triangles (scene-aware size)
        const dir = new THREE.Vector3().subVectors(offsetB, offsetA).normalize();
        this.addArrowTriangle(arrowPositions, offsetA, dir, offsetDir, sceneArrow);
        this.addArrowTriangle(arrowPositions, offsetB, dir.clone().negate(), offsetDir, sceneArrow);

        // Label at midpoint
        const midpoint = new THREE.Vector3().addVectors(offsetA, offsetB).multiplyScalar(0.5);
        const label = this.createLabel(line, midpoint);
        this.labels.push({
          element: label,
          midpoint3D: midpoint,
          dimKey: line.dimKey,
          paramFrom: line.paramFrom,
          paramTo: line.paramTo,
          value: line.value,
          vectorContext: line.vectorContext,
        });
      }

      // Dimension lines
      this.dimLineGeometry = new THREE.BufferGeometry();
      this.dimLineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
      const lineSegments = new THREE.LineSegments(this.dimLineGeometry, this.lineMaterial);
      lineSegments.renderOrder = 10;
      lineSegments.frustumCulled = false;
      this.lineGroup.add(lineSegments);

      // Filled arrowheads
      if (arrowPositions.length > 0) {
        this.dimArrowGeometry = new THREE.BufferGeometry();
        this.dimArrowGeometry.setAttribute('position', new THREE.Float32BufferAttribute(arrowPositions, 3));
        const arrowMesh = new THREE.Mesh(this.dimArrowGeometry, this.arrowMaterial);
        arrowMesh.renderOrder = 10;
        arrowMesh.frustumCulled = false;
        this.lineGroup.add(arrowMesh);
      }

      this.scene.add(this.lineGroup);
    }

    // Build handle divs
    for (const handle of setup.handles) {
      const entry = this.createHandle(handle);
      this.handles.push(entry);
    }
  }

  updateProjections(): void {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    if (width === 0 || height === 0) return;

    const vec = new THREE.Vector3();

    for (const entry of this.labels) {
      vec.copy(entry.midpoint3D);
      vec.project(this.camera);

      if (vec.z > 1) {
        entry.element.style.display = 'none';
        continue;
      }

      entry.element.style.display = '';
      const x = (vec.x * 0.5 + 0.5) * width;
      const y = (-vec.y * 0.5 + 0.5) * height;
      entry.element.style.left = x + 'px';
      entry.element.style.top = y + 'px';
    }

    // Only update non-dragged handles
    for (const entry of this.handles) {
      if (this.dragState && this.dragState.handleEntry === entry) continue;

      vec.copy(entry.position3D);
      vec.project(this.camera);

      if (vec.z > 1) {
        entry.element.style.display = 'none';
        continue;
      }

      entry.element.style.display = '';
      const x = (vec.x * 0.5 + 0.5) * width;
      const y = (-vec.y * 0.5 + 0.5) * height;
      entry.element.style.left = x + 'px';
      entry.element.style.top = y + 'px';
    }
  }

  clear(): void {
    this.cancelDrag();

    if (this.lineGroup) {
      this.scene.remove(this.lineGroup);
      for (const child of this.lineGroup.children) {
        if (child instanceof THREE.LineSegments || child instanceof THREE.Mesh) {
          child.geometry.dispose();
        }
      }
      this.lineGroup = null;
    }

    if (this.previewWireframe) {
      this.scene.remove(this.previewWireframe);
      this.previewWireframe.geometry.dispose();
      this.previewWireframe = null;
    }
    this.originalBBox = null;
    this.storedLines = [];
    this.dimLineGeometry = null;
    this.dimArrowGeometry = null;

    for (const entry of this.labels) {
      if (entry.element.parentNode) {
        entry.element.parentNode.removeChild(entry.element);
      }
    }
    this.labels = [];

    for (const entry of this.handles) {
      if (entry.element.parentNode) {
        entry.element.parentNode.removeChild(entry.element);
      }
    }
    this.handles = [];
  }

  setVisible(visible: boolean): void {
    this.overlayDiv.style.display = visible ? '' : 'none';
    if (this.lineGroup) this.lineGroup.visible = visible;
  }

  dispose(): void {
    this.clear();
    this.lineMaterial.dispose();
    this.arrowMaterial.dispose();
    this.previewWireframeMaterial.dispose();
    if (this.overlayDiv.parentNode) {
      this.overlayDiv.parentNode.removeChild(this.overlayDiv);
    }
  }

  // --- Dimension line rendering helpers ---

  /** Adds a filled triangle arrowhead (3 vertices for one triangle face). */
  private addArrowTriangle(positions: number[], tip: THREE.Vector3, inward: THREE.Vector3, perpDir: THREE.Vector3, size: number = ARROW_SIZE): void {
    const up = perpDir.clone().normalize();
    const arm1 = inward.clone().multiplyScalar(size);
    const arm2 = arm1.clone();

    const axis = new THREE.Vector3().crossVectors(inward, up).normalize();
    if (axis.lengthSq() < 0.001) {
      axis.set(0, 1, 0);
      if (Math.abs(inward.dot(axis)) > 0.9) axis.set(1, 0, 0);
      axis.crossVectors(inward, axis).normalize();
    }

    arm1.applyAxisAngle(axis, ARROW_ANGLE);
    arm2.applyAxisAngle(axis, -ARROW_ANGLE);

    const end1 = tip.clone().add(arm1);
    const end2 = tip.clone().add(arm2);

    // Triangle face: tip, end1, end2
    positions.push(tip.x, tip.y, tip.z);
    positions.push(end1.x, end1.y, end1.z);
    positions.push(end2.x, end2.y, end2.z);
  }

  // --- Live dimension line update during drag ---

  /** Remap a point from original bbox space to new bbox space. */
  private remapBBoxPoint(p: THREE.Vector3, newMin: THREE.Vector3, newMax: THREE.Vector3): THREE.Vector3 {
    const orig = this.originalBBox!;
    const result = p.clone();
    for (const c of ['x', 'y', 'z'] as const) {
      const range = orig.max[c] - orig.min[c];
      if (range < 0.001) continue;
      const t = (p[c] - orig.min[c]) / range;
      result[c] = newMin[c] + t * (newMax[c] - newMin[c]);
    }
    return result;
  }

  /** Compute arrow triangle vertices (same logic as addArrowTriangle, returns points). */
  private arrowTriangleVertices(tip: THREE.Vector3, inward: THREE.Vector3, perpDir: THREE.Vector3): [THREE.Vector3, THREE.Vector3, THREE.Vector3] {
    const up = perpDir.clone().normalize();
    const arm1 = inward.clone().multiplyScalar(ARROW_SIZE);
    const arm2 = arm1.clone();
    const axis = new THREE.Vector3().crossVectors(inward, up).normalize();
    if (axis.lengthSq() < 0.001) {
      axis.set(0, 1, 0);
      if (Math.abs(inward.dot(axis)) > 0.9) axis.set(1, 0, 0);
      axis.crossVectors(inward, axis).normalize();
    }
    arm1.applyAxisAngle(axis, ARROW_ANGLE);
    arm2.applyAxisAngle(axis, -ARROW_ANGLE);
    return [tip.clone(), tip.clone().add(arm1), tip.clone().add(arm2)];
  }

  /** Update dimension line geometry and label positions to match a new bbox. */
  private updateDimensionLines(newMin: THREE.Vector3, newMax: THREE.Vector3): void {
    if (!this.originalBBox || !this.dimLineGeometry || this.storedLines.length === 0) return;

    const lineAttr = this.dimLineGeometry.getAttribute('position') as THREE.BufferAttribute;
    const lineBuf = lineAttr.array as Float32Array;
    const arrowAttr = this.dimArrowGeometry?.getAttribute('position') as THREE.BufferAttribute | undefined;
    const arrowBuf = arrowAttr?.array as Float32Array | undefined;

    let lo = 0; // line buffer offset
    let ao = 0; // arrow buffer offset

    for (let i = 0; i < this.storedLines.length; i++) {
      const line = this.storedLines[i];

      const start = this.remapBBoxPoint(line.start, newMin, newMax);
      const end = this.remapBBoxPoint(line.end, newMin, newMax);
      const { offsetDir, offsetDist } = line;

      const offsetA = start.clone().addScaledVector(offsetDir, offsetDist);
      const offsetB = end.clone().addScaledVector(offsetDir, offsetDist);
      const extEndA = start.clone().addScaledVector(offsetDir, offsetDist + EXTENSION_OVERSHOOT);
      const extEndB = end.clone().addScaledVector(offsetDir, offsetDist + EXTENSION_OVERSHOOT);

      // Extension line A (2 verts = 6 floats)
      lineBuf[lo++] = start.x; lineBuf[lo++] = start.y; lineBuf[lo++] = start.z;
      lineBuf[lo++] = extEndA.x; lineBuf[lo++] = extEndA.y; lineBuf[lo++] = extEndA.z;
      // Extension line B
      lineBuf[lo++] = end.x; lineBuf[lo++] = end.y; lineBuf[lo++] = end.z;
      lineBuf[lo++] = extEndB.x; lineBuf[lo++] = extEndB.y; lineBuf[lo++] = extEndB.z;
      // Measurement line
      lineBuf[lo++] = offsetA.x; lineBuf[lo++] = offsetA.y; lineBuf[lo++] = offsetA.z;
      lineBuf[lo++] = offsetB.x; lineBuf[lo++] = offsetB.y; lineBuf[lo++] = offsetB.z;

      // Arrow triangles
      if (arrowBuf) {
        const dir = new THREE.Vector3().subVectors(offsetB, offsetA).normalize();
        const triA = this.arrowTriangleVertices(offsetA, dir, offsetDir);
        for (const v of triA) { arrowBuf[ao++] = v.x; arrowBuf[ao++] = v.y; arrowBuf[ao++] = v.z; }
        const triB = this.arrowTriangleVertices(offsetB, dir.clone().negate(), offsetDir);
        for (const v of triB) { arrowBuf[ao++] = v.x; arrowBuf[ao++] = v.y; arrowBuf[ao++] = v.z; }
      }

      // Update label midpoint
      if (i < this.labels.length) {
        const midpoint = new THREE.Vector3().addVectors(offsetA, offsetB).multiplyScalar(0.5);
        this.labels[i].midpoint3D.copy(midpoint);
      }
    }

    lineAttr.needsUpdate = true;
    if (arrowAttr) arrowAttr.needsUpdate = true;
  }

  // --- Label creation & editing ---

  private createLabel(line: DimensionLine, _midpoint: THREE.Vector3): HTMLDivElement {
    const label = document.createElement('div');
    label.className = 'dimension-label';
    if (line.readonly) label.classList.add('dimension-label-readonly');

    const span = document.createElement('span');
    const num = line.value.toFixed(2);
    span.textContent = line.labelPrefix ? `${line.labelPrefix} ${num}` : num;
    label.appendChild(span);

    if (!line.readonly) {
      label.addEventListener('click', (e) => {
        e.stopPropagation();
        this.startEdit(label, line);
      });
    } else {
      label.style.cursor = 'default';
    }

    this.overlayDiv.appendChild(label);
    return label;
  }

  private startEdit(label: HTMLDivElement, line: DimensionLine): void {
    if (label.querySelector('input')) return;

    const span = label.querySelector('span');
    if (!span) return;

    const input = document.createElement('input');
    input.type = 'text';
    input.value = line.value.toFixed(2);
    input.style.width = Math.max(48, input.value.length * 8) + 'px';

    label.replaceChild(input, span);
    input.select();
    input.focus();

    const commit = () => {
      const parsed = parseFloat(input.value);
      if (!isNaN(parsed) && parsed !== line.value) {
        this.commitValue(parsed, line.paramFrom, line.paramTo, line.vectorContext);
      } else {
        this.restoreSpan(label, line.value);
      }
    };

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.restoreSpan(label, line.value);
      }
    });

    input.addEventListener('blur', () => {
      if (label.contains(input)) {
        commit();
      }
    });
  }

  private restoreSpan(label: HTMLDivElement, value: number): void {
    const input = label.querySelector('input');
    if (!input) return;

    const span = document.createElement('span');
    span.textContent = value.toFixed(2);
    label.replaceChild(span, input);
  }

  // --- Wireframe box helpers ---

  private buildWireframeBox(min: THREE.Vector3, max: THREE.Vector3, material: THREE.LineBasicMaterial): THREE.LineSegments {
    const positions = new Float32Array(72);
    this.setBoxEdgePositions(positions, min, max);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const lines = new THREE.LineSegments(geometry, material);
    lines.renderOrder = 11;
    lines.frustumCulled = false;
    return lines;
  }

  private setBoxEdgePositions(buf: Float32Array, min: THREE.Vector3, max: THREE.Vector3): void {
    const edges = [
      // Bottom face
      min.x, min.y, min.z, max.x, min.y, min.z,
      max.x, min.y, min.z, max.x, min.y, max.z,
      max.x, min.y, max.z, min.x, min.y, max.z,
      min.x, min.y, max.z, min.x, min.y, min.z,
      // Top face
      min.x, max.y, min.z, max.x, max.y, min.z,
      max.x, max.y, min.z, max.x, max.y, max.z,
      max.x, max.y, max.z, min.x, max.y, max.z,
      min.x, max.y, max.z, min.x, max.y, min.z,
      // Vertical edges
      min.x, min.y, min.z, min.x, max.y, min.z,
      max.x, min.y, min.z, max.x, max.y, min.z,
      max.x, min.y, max.z, max.x, max.y, max.z,
      min.x, min.y, max.z, min.x, max.y, max.z,
    ];
    for (let i = 0; i < edges.length; i++) {
      buf[i] = edges[i];
    }
  }

  private updateWireframeBox(lineSegments: THREE.LineSegments, min: THREE.Vector3, max: THREE.Vector3): void {
    const positions = lineSegments.geometry.getAttribute('position') as THREE.BufferAttribute;
    this.setBoxEdgePositions(positions.array as Float32Array, min, max);
    positions.needsUpdate = true;
  }

  // --- Commit value ---

  private commitValue(parsed: number, paramFrom: number, paramTo: number, vectorContext?: VectorContext): void {
    if (vectorContext) {
      const vals = [...vectorContext.values];
      vals[vectorContext.editIndex] = parsed;
      const vecStr = '[' + vals.map(v => {
        const s = v.toFixed(2);
        return parseFloat(s).toString();
      }).join(', ') + ']';
      this.onEdit(vectorContext.fullFrom, vectorContext.fullTo, vecStr);
    } else {
      const valStr = parseFloat(parsed.toFixed(2)).toString();
      this.onEdit(paramFrom, paramTo, valStr);
    }
  }

  // --- Handle creation ---

  private createHandle(handle: DragHandle): HandleEntry {
    const el = document.createElement('div');
    el.className = 'dimension-handle';
    this.overlayDiv.appendChild(el);

    const entry: HandleEntry = {
      element: el,
      handle,
      position3D: handle.position.clone(),
    };

    el.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.startDrag(entry, e);
    });

    return entry;
  }

  // --- Drag interaction (resize along locked axis) ---

  private startDrag(entry: HandleEntry, e: PointerEvent): void {
    entry.element.setPointerCapture(e.pointerId);
    entry.element.classList.add('dragging');

    this.onDragStateChange(true);

    // Multi-axis: corner handles with 2+ horizontal axes and vector context
    const isMultiAxis = entry.handle.axes.length >= 2 &&
      entry.handle.axes.every(a => Math.abs(a.axis.y) < 0.5) &&
      entry.handle.axes[0].vectorContext !== undefined;

    this.dragState = {
      handleEntry: entry,
      startMouse: { x: e.clientX, y: e.clientY },
      startPosition3D: entry.position3D.clone(),
      lockedAxisIndex: null,
      startValues: entry.handle.axes.map(a => a.value),
      pointerId: e.pointerId,
      currentNewValue: null,
      multiAxis: isMultiAxis,
      currentNewValues: entry.handle.axes.map(a => a.value),
    };

    // Create preview wireframe (shows new bbox during drag)
    if (this.originalBBox) {
      this.previewWireframe = this.buildWireframeBox(
        this.originalBBox.min, this.originalBBox.max, this.previewWireframeMaterial,
      );
      this.scene.add(this.previewWireframe);
    }

    // Create tooltip
    this.dragTooltip = document.createElement('div');
    this.dragTooltip.className = 'dimension-label';
    this.dragTooltip.style.display = 'none';
    this.overlayDiv.appendChild(this.dragTooltip);

    entry.element.addEventListener('pointermove', this.onPointerMove);
    entry.element.addEventListener('pointerup', this.onPointerUp);
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.dragState) return;

    const ds = this.dragState;
    const handle = ds.handleEntry.handle;

    // Set up ray from mouse
    const rect = this.container.getBoundingClientRect();
    const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.camera);

    if (ds.multiAxis) {
      this.handleMultiAxisMove(ds);
    } else {
      this.handleSingleAxisMove(ds, e);
    }
  };

  /** Multi-axis drag: corner handles move freely on the XZ working plane */
  private handleMultiAxisMove(ds: DragState): void {
    const handle = ds.handleEntry.handle;

    // Intersect ray with the working plane
    const hit = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.workPlane, hit)) return;

    const newPos3D = ds.startPosition3D.clone();
    const newMin = this.originalBBox ? this.originalBBox.min.clone() : undefined;
    const newMax = this.originalBBox ? this.originalBBox.max.clone() : undefined;

    // Process each axis (X and Z) simultaneously
    for (let i = 0; i < handle.axes.length; i++) {
      const axisEntry = handle.axes[i];
      const comp: 'x' | 'z' = Math.abs(axisEntry.axis.x) > 0.5 ? 'x' : 'z';

      // Check if this axis is blocked by keyboard constraint or toolbar lock
      const blocked =
        (this.keyboardAxisConstraint !== null && this.keyboardAxisConstraint !== comp) ||
        !this.axisEnabled[comp];

      if (blocked) {
        // Keep start value — axis doesn't move
        ds.currentNewValues[i] = ds.startValues[i];
        this.updateLabelText(axisEntry.dimKey, ds.startValues[i]);
        continue;
      }

      const mouseAxisPos = hit[comp];

      let newValue: number;
      let clampedAxisPos: number;

      if (axisEntry.isCenteredParam && this.originalBBox) {
        // Sphere/cylinder: ratio-based drag from bbox center
        const bboxCenter = (this.originalBBox.min[comp] + this.originalBBox.max[comp]) / 2;
        const startDist = ds.startPosition3D[comp] - bboxCenter;
        const mouseDist = mouseAxisPos - bboxCenter;
        const startVal = ds.startValues[i];
        if (Math.abs(startDist) > 0.0001 && startVal > 0.0001) {
          newValue = this.snap(Math.max(startVal * (mouseDist / startDist), 0.01));
        } else {
          newValue = this.snap(Math.max((mouseAxisPos - bboxCenter) * axisEntry.sign, 0.01));
        }
        const ratio = startVal > 0.0001 ? newValue / startVal : 1;
        clampedAxisPos = bboxCenter + startDist * ratio;
      } else {
        const fixedSide = this.originalBBox
          ? (axisEntry.sign > 0 ? this.originalBBox.min[comp] : this.originalBBox.max[comp])
          : ds.startPosition3D[comp] - axisEntry.sign * ds.startValues[i];
        const rawExtent = (mouseAxisPos - fixedSide) * axisEntry.sign;
        newValue = this.snap(rawExtent);
        clampedAxisPos = fixedSide + newValue * axisEntry.sign;
      }

      ds.currentNewValues[i] = newValue;
      newPos3D[comp] = clampedAxisPos;

      // Update dimension label for this axis
      this.updateLabelText(axisEntry.dimKey, newValue);

      // Update wireframe on this axis
      if (newMin && newMax) {
        if (axisEntry.isCenteredParam) {
          const bboxCenter = (this.originalBBox!.min[comp] + this.originalBBox!.max[comp]) / 2;
          const halfExtent = Math.abs(clampedAxisPos - bboxCenter);
          newMin[comp] = bboxCenter - halfExtent;
          newMax[comp] = bboxCenter + halfExtent;
        } else if (axisEntry.sign > 0) {
          newMax[comp] = clampedAxisPos;
        } else {
          newMin[comp] = clampedAxisPos;
        }
      }
    }

    ds.handleEntry.position3D.copy(newPos3D);

    // Update wireframe preview and dimension lines
    if (newMin && newMax) {
      if (this.previewWireframe) {
        this.updateWireframeBox(this.previewWireframe, newMin, newMax);
      }
      this.updateDimensionLines(newMin, newMax);
    }

    // Project handle to screen
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    const projected = newPos3D.clone().project(this.camera);
    const screenX = (projected.x * 0.5 + 0.5) * width;
    const screenY = (-projected.y * 0.5 + 0.5) * height;
    ds.handleEntry.element.style.left = screenX + 'px';
    ds.handleEntry.element.style.top = screenY + 'px';

    // Tooltip shows both values
    if (this.dragTooltip) {
      const parts = ds.currentNewValues.map(v => parseFloat(v.toFixed(2)).toString());
      this.dragTooltip.textContent = parts.join(' \u00d7 ');
      this.dragTooltip.style.display = '';
      this.dragTooltip.style.left = (screenX + 14) + 'px';
      this.dragTooltip.style.top = (screenY - 8) + 'px';
    }
  }

  /** Single-axis drag: locks to one axis after threshold (used for height, scalar cubes) */
  private handleSingleAxisMove(ds: DragState, e: PointerEvent): void {
    const handle = ds.handleEntry.handle;

    // Mouse pixel delta from start (for axis lock threshold)
    const mouseDx = e.clientX - ds.startMouse.x;
    const mouseDy = e.clientY - ds.startMouse.y;
    const mouseDist = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);

    // Axis locking: use working plane to detect dominant axis
    if (ds.lockedAxisIndex === null) {
      const lockHit = new THREE.Vector3();
      if (!this.raycaster.ray.intersectPlane(this.workPlane, lockHit)) return;
      if (mouseDist < AXIS_LOCK_THRESHOLD) return;

      const delta3D = lockHit.clone().sub(ds.startPosition3D);
      if (handle.axes.length === 1) {
        ds.lockedAxisIndex = 0;
      } else {
        let bestIdx = 0;
        let bestProj = -Infinity;
        for (let i = 0; i < handle.axes.length; i++) {
          // Skip disabled axes when choosing best axis
          const axComp: 'x' | 'y' | 'z' =
            Math.abs(handle.axes[i].axis.x) > 0.5 ? 'x' :
            Math.abs(handle.axes[i].axis.y) > 0.5 ? 'y' : 'z';
          if (!this.axisEnabled[axComp]) continue;
          const proj = Math.abs(delta3D.dot(handle.axes[i].axis));
          if (proj > bestProj) {
            bestProj = proj;
            bestIdx = i;
          }
        }
        ds.lockedAxisIndex = bestIdx;
      }
    }

    const axisEntry = handle.axes[ds.lockedAxisIndex];
    const comp: 'x' | 'y' | 'z' =
      Math.abs(axisEntry.axis.x) > 0.5 ? 'x' :
      Math.abs(axisEntry.axis.y) > 0.5 ? 'y' : 'z';

    // Check if this axis is blocked by keyboard constraint or toolbar lock
    const blocked =
      (this.keyboardAxisConstraint !== null && this.keyboardAxisConstraint !== comp) ||
      !this.axisEnabled[comp];
    if (blocked) return;

    // Intersect with the right plane depending on the axis
    const hit = new THREE.Vector3();
    if (comp === 'y') {
      // Y axis (height): use a vertical plane facing the camera
      const camDir = this.camera.getWorldDirection(new THREE.Vector3());
      const planeNormal = new THREE.Vector3(camDir.x, 0, camDir.z);
      if (planeNormal.lengthSq() < 0.001) planeNormal.set(0, 0, 1);
      planeNormal.normalize();
      const vertPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, ds.startPosition3D);
      if (!this.raycaster.ray.intersectPlane(vertPlane, hit)) return;
    } else {
      // X or Z axis: use the horizontal working plane
      if (!this.raycaster.ray.intersectPlane(this.workPlane, hit)) return;
    }

    // Mouse 3D position along the locked axis
    const mouseAxisPos = hit[comp];

    let newValue: number;
    let clampedAxisPos: number;

    if (axisEntry.isCenteredParam && this.originalBBox) {
      // Sphere/cylinder: ratio-based drag from bbox center
      const bboxCenter = (this.originalBBox.min[comp] + this.originalBBox.max[comp]) / 2;
      const startDist = ds.startPosition3D[comp] - bboxCenter;
      const mouseDist = mouseAxisPos - bboxCenter;
      const startVal = ds.startValues[ds.lockedAxisIndex];
      if (Math.abs(startDist) > 0.0001 && startVal > 0.0001) {
        newValue = this.snap(Math.max(startVal * (mouseDist / startDist), 0.01));
      } else {
        newValue = this.snap(Math.max((mouseAxisPos - bboxCenter) * axisEntry.sign, 0.01));
      }
      const ratio = startVal > 0.0001 ? newValue / startVal : 1;
      clampedAxisPos = bboxCenter + startDist * ratio;
    } else {
      // Cubes and other shapes: value = distance from opposite face
      const fixedSide = this.originalBBox
        ? (axisEntry.sign > 0 ? this.originalBBox.min[comp] : this.originalBBox.max[comp])
        : ds.startPosition3D[comp] - axisEntry.sign * ds.startValues[ds.lockedAxisIndex];
      const rawExtent = (mouseAxisPos - fixedSide) * axisEntry.sign;
      newValue = this.snap(rawExtent);
      clampedAxisPos = fixedSide + newValue * axisEntry.sign;
    }

    ds.currentNewValue = newValue;
    const newPos3D = ds.startPosition3D.clone();
    newPos3D[comp] = clampedAxisPos;
    ds.handleEntry.position3D.copy(newPos3D);

    // Project handle to screen
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    const projected = newPos3D.clone().project(this.camera);
    const screenX = (projected.x * 0.5 + 0.5) * width;
    const screenY = (-projected.y * 0.5 + 0.5) * height;
    ds.handleEntry.element.style.left = screenX + 'px';
    ds.handleEntry.element.style.top = screenY + 'px';

    // Update dimension label text
    this.updateLabelText(axisEntry.dimKey, newValue);

    // Update preview wireframe and dimension lines
    if (this.originalBBox) {
      const newMin = this.originalBBox.min.clone();
      const newMax = this.originalBBox.max.clone();
      if (axisEntry.isCenteredParam) {
        // Centered: both sides move symmetrically from center
        const bboxCenter = (this.originalBBox.min[comp] + this.originalBBox.max[comp]) / 2;
        const halfExtent = Math.abs(clampedAxisPos - bboxCenter);
        newMin[comp] = bboxCenter - halfExtent;
        newMax[comp] = bboxCenter + halfExtent;
      } else if (axisEntry.sign > 0) {
        newMax[comp] = clampedAxisPos;
      } else {
        newMin[comp] = clampedAxisPos;
      }
      if (this.previewWireframe) {
        this.updateWireframeBox(this.previewWireframe, newMin, newMax);
      }
      this.updateDimensionLines(newMin, newMax);
    }

    // Update tooltip near the handle
    if (this.dragTooltip) {
      const formatted = parseFloat(newValue.toFixed(2)).toString();
      this.dragTooltip.textContent = formatted;
      this.dragTooltip.style.display = '';
      this.dragTooltip.style.left = (screenX + 14) + 'px';
      this.dragTooltip.style.top = (screenY - 8) + 'px';
    }
  }

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.dragState) return;

    const ds = this.dragState;
    const handle = ds.handleEntry.handle;

    ds.handleEntry.element.releasePointerCapture(e.pointerId);
    ds.handleEntry.element.classList.remove('dragging');
    ds.handleEntry.element.removeEventListener('pointermove', this.onPointerMove);
    ds.handleEntry.element.removeEventListener('pointerup', this.onPointerUp);

    this.onDragStateChange(false);
    this.removeDragPreview();
    this.keyboardAxisConstraint = null;

    if (ds.multiAxis) {
      // Multi-axis commit: update all changed vector components at once
      const anyChanged = ds.currentNewValues.some(
        (v, i) => Math.abs(v - ds.startValues[i]) > 0.001,
      );
      if (anyChanged && handle.axes[0].vectorContext) {
        const baseValues = [...handle.axes[0].vectorContext.values];
        for (let i = 0; i < handle.axes.length; i++) {
          const vc = handle.axes[i].vectorContext;
          if (vc) baseValues[vc.editIndex] = ds.currentNewValues[i];
        }
        const vecStr = '[' + baseValues.map(v => {
          const s = v.toFixed(2);
          return parseFloat(s).toString();
        }).join(', ') + ']';
        const vc = handle.axes[0].vectorContext;
        this.onEdit(vc.fullFrom, vc.fullTo, vecStr);
      }
    } else if (ds.lockedAxisIndex !== null && ds.currentNewValue !== null) {
      const axisEntry = handle.axes[ds.lockedAxisIndex];
      const startValue = ds.startValues[ds.lockedAxisIndex];

      if (Math.abs(ds.currentNewValue - startValue) > 0.001) {
        this.commitValue(ds.currentNewValue, axisEntry.paramFrom, axisEntry.paramTo, axisEntry.vectorContext);
      }
    }

    this.dragState = null;
  };

  private cancelDrag(): void {
    if (!this.dragState) return;

    const ds = this.dragState;
    ds.handleEntry.element.releasePointerCapture(ds.pointerId);
    ds.handleEntry.element.classList.remove('dragging');
    ds.handleEntry.element.removeEventListener('pointermove', this.onPointerMove);
    ds.handleEntry.element.removeEventListener('pointerup', this.onPointerUp);
    this.onDragStateChange(false);
    this.removeDragPreview();
    this.keyboardAxisConstraint = null;
    this.dragState = null;
  }

  private removeDragPreview(): void {
    if (this.previewWireframe) {
      this.scene.remove(this.previewWireframe);
      this.previewWireframe.geometry.dispose();
      this.previewWireframe = null;
    }
    if (this.dragTooltip) {
      if (this.dragTooltip.parentNode) {
        this.dragTooltip.parentNode.removeChild(this.dragTooltip);
      }
      this.dragTooltip = null;
    }
  }

  private updateLabelText(dimKey: string, value: number): void {
    for (const entry of this.labels) {
      if (entry.dimKey === dimKey) {
        const span = entry.element.querySelector('span');
        if (span) {
          span.textContent = value.toFixed(2);
        }
        break;
      }
    }
  }
}
